import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

const glow = keyframes`
  0%, 100% {
    box-shadow: -5px 0 15px rgba(255, 105, 180, 0.3),
                -5px 0 25px rgba(33, 150, 243, 0.2);
  }
  50% {
    box-shadow: -5px 0 25px rgba(255, 105, 180, 0.4),
                -5px 0 35px rgba(33, 150, 243, 0.3);
  }
`;

const scanline = keyframes`
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(100%);
  }
`;

const Container = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  right: ${props => props.$isOpen ? '0' : '-200px'};
  top: 50%;
  transform: translateY(-50%);
  width: 220px;
  background: rgba(15, 17, 26, 0.95);
  border-left: 2px solid #ff69b4;
  border-radius: 12px 0 0 12px;
  padding: 1.5rem 1rem 1.5rem 3rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  animation: ${glow} 4s infinite;
  backdrop-filter: blur(10px);

  @media (max-width: 1200px) and (min-width: 769px) {
    width: 180px;
    right: ${props => props.$isOpen ? '0' : '-160px'};
    padding: 1rem 0.5rem 1rem 2.5rem;
  }

  @media (max-width: 768px) {
    width: 100%;
    right: ${props => props.$isOpen ? '0' : '-100%'};
    border-radius: 12px 12px 0 0;
    border-left: none;
    border-top: 2px solid #ff69b4;
    top: auto;
    bottom: 0;
    transform: none;
    padding: 1.5rem;
  }

  /* Scanline effect */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
      0deg,
      transparent,
      transparent 2px,
      rgba(33, 150, 243, 0.05) 3px,
      transparent 4px
    );
    pointer-events: none;
    opacity: 0.1;
    animation: ${scanline} 10s linear infinite;
    border-radius: 12px 0 0 12px;

    @media (max-width: 768px) {
      border-radius: 12px 12px 0 0;
    }
  }

  /* Terminal frame effect */
  &::after {
    content: '';
    position: absolute;
    inset: 1px;
    border: 1px solid rgba(33, 150, 243, 0.3);
    border-radius: 11px 0 0 11px;
    pointer-events: none;
    opacity: 0.5;

    @media (max-width: 768px) {
      border-radius: 11px 11px 0 0;
    }
  }
`;

const ToggleButton = styled.button`
  position: absolute;
  left: 1.25rem;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(15, 17, 26, 0.95);
  border: 2px solid #ff69b4;
  border-radius: 6px;
  color: #ff69b4;
  font-family: 'Share Tech Mono', monospace;
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1.2rem;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  box-shadow: 0 0 10px rgba(255, 105, 180, 0.2);

  @media (max-width: 1200px) and (min-width: 769px) {
    left: 1rem;
    width: 32px;
    height: 32px;
    padding: 0.4rem;
    font-size: 1rem;
  }

  @media (max-width: 768px) {
    top: 0;
    left: 50%;
    transform: translate(-50%, -100%);
    border-radius: 6px 6px 0 0;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 2px;
    border: 1px solid rgba(33, 150, 243, 0.3);
    border-radius: 4px;
    opacity: 0.5;

    @media (max-width: 768px) {
      border-radius: 4px 4px 0 0;
    }
  }

  &:hover {
    color: #2196f3;
    border-color: #2196f3;
    text-shadow: 0 0 10px rgba(33, 150, 243, 0.5);
    box-shadow: 0 0 15px rgba(33, 150, 243, 0.3);
  }
`;

const LinkList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  padding-left: 0.75rem;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    padding: 0;
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: #fff;
  text-decoration: none;
  font-family: 'Share Tech Mono', monospace;
  padding: 0.6rem 0.8rem;
  border: 1px solid rgba(255, 105, 180, 0.3);
  border-radius: 4px;
  transition: all 0.3s ease;
  background: rgba(15, 17, 26, 0.6);
  position: relative;
  font-size: 0.85rem;
  white-space: nowrap;
  min-width: 0;

  /* Terminal line effect */
  &::before {
    content: '';
    position: absolute;
    inset: 1px;
    border: 1px solid rgba(33, 150, 243, 0.2);
    border-radius: 3px;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  i {
    width: 24px;
    text-align: center;
    color: #ff69b4;
    text-shadow: 0 0 10px rgba(255, 105, 180, 0.5);
    transition: all 0.3s ease;
    font-size: 1.1rem;
  }

  &:hover {
    background: rgba(33, 150, 243, 0.1);
    border-color: #2196f3;
    transform: translateX(-5px);
    box-shadow: 0 0 15px rgba(33, 150, 243, 0.1);

    &::before {
      opacity: 1;
    }

    i {
      color: #2196f3;
      text-shadow: 0 0 10px rgba(33, 150, 243, 0.5);
      transform: scale(1.1);
    }
  }

  @media (max-width: 1200px) and (min-width: 769px) {
    padding: 0.4rem 0.3rem;
    font-size: 0.7rem;
    gap: 0.4rem;
    white-space: nowrap;
    min-width: 0;
    flex-shrink: 1;
  }

  @media (max-width: 768px) {
    padding: 0.8rem;
    font-size: 0.8rem;
    justify-content: center;
  }
`;

const YouTubeDropdown = styled.div`
  position: relative;
`;

const YouTubeButton = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: #fff;
  text-decoration: none;
  font-family: 'Share Tech Mono', monospace;
  padding: 0.6rem 0.8rem;
  border: 1px solid rgba(255, 105, 180, 0.3);
  border-radius: 4px;
  transition: all 0.3s ease;
  background: rgba(15, 17, 26, 0.6);
  position: relative;
  font-size: 0.85rem;
  white-space: nowrap;
  min-width: 0;
  cursor: pointer;

  &::before {
    content: '';
    position: absolute;
    inset: 1px;
    border: 1px solid rgba(33, 150, 243, 0.2);
    border-radius: 3px;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  i {
    width: 24px;
    text-align: center;
    color: #ff69b4;
    text-shadow: 0 0 10px rgba(255, 105, 180, 0.5);
    transition: all 0.3s ease;
    font-size: 1.1rem;
  }

  &:hover {
    background: rgba(33, 150, 243, 0.1);
    border-color: #2196f3;
    transform: translateX(-5px);
    box-shadow: 0 0 15px rgba(33, 150, 243, 0.1);

    &::before {
      opacity: 1;
    }

    i {
      color: #2196f3;
      text-shadow: 0 0 10px rgba(33, 150, 243, 0.5);
      transform: scale(1.1);
    }
  }

  @media (max-width: 1200px) and (min-width: 769px) {
    padding: 0.4rem 0.3rem;
    font-size: 0.7rem;
    gap: 0.4rem;
  }

  @media (max-width: 768px) {
    padding: 0.8rem;
    font-size: 0.8rem;
    justify-content: center;
  }
`;

const YouTubeSubMenu = styled.div<{ $open: boolean }>`
  display: ${props => props.$open ? 'flex' : 'none'};
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 0.4rem;
  padding-left: 2rem;

  @media (max-width: 768px) {
    padding-left: 0;
  }
`;

const YouTubeSubLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: #ccc;
  text-decoration: none;
  font-family: 'Share Tech Mono', monospace;
  padding: 0.4rem 0.6rem;
  border: 1px solid rgba(255, 105, 180, 0.15);
  border-radius: 4px;
  transition: all 0.3s ease;
  background: rgba(15, 17, 26, 0.8);
  font-size: 0.75rem;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 0, 0, 0.1);
    border-color: #ff0000;
    color: #fff;
    transform: translateX(-3px);
    box-shadow: 0 0 10px rgba(255, 0, 0, 0.15);
  }

  @media (max-width: 1200px) and (min-width: 769px) {
    padding: 0.3rem 0.4rem;
    font-size: 0.65rem;
  }

  @media (max-width: 768px) {
    padding: 0.6rem;
    font-size: 0.75rem;
    justify-content: center;
  }
`;

const socialLinks = [
  { icon: 'fab fa-patreon', label: 'Patreon', url: 'https://www.patreon.com/c/A5omic' },
  { icon: 'fab fa-discord', label: 'Discord', url: 'https://discord.com/invite/GJJrgsh4Tx' },
  { icon: 'fab fa-instagram', label: 'Instagram', url: 'https://www.instagram.com/a5omic' },
  { icon: 'fab fa-tiktok', label: 'TikTok', url: 'https://www.tiktok.com/@a5omic' },
  { icon: 'fab fa-spotify', label: 'Spotify', url: 'https://open.spotify.com/artist/7L3rQbwQVEGa71Ign85jyd' },
  { icon: 'fab fa-reddit', label: 'Reddit', url: 'https://www.reddit.com/u/Major-Design-5332' },
  { icon: 'fab fa-github', label: 'GitHub', url: 'https://github.com/Atomics-hub' },
  { icon: 'fab fa-facebook', label: 'Facebook', url: 'https://www.facebook.com/people/Tom-Ryan/100084682343532/' },
  { icon: 'fas fa-envelope', label: 'Email', url: 'mailto:atomicmbeats@gmail.com' },
];

const SocialLinks: React.FC = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [ytOpen, setYtOpen] = useState(false);
  const ytRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ytRef.current && !ytRef.current.contains(e.target as Node)) {
        setYtOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleContainerClick = () => {
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <Container $isOpen={isOpen} onClick={handleContainerClick} style={{ cursor: !isOpen ? 'pointer' : 'default' }}>
      <ToggleButton onClick={handleToggleClick}>
        <i className={`fas fa-chevron-${isOpen ? 'right' : 'left'}`} />
      </ToggleButton>
      <LinkList>
        <SocialLink href="https://x.com/A5omic" target="_blank" rel="noopener noreferrer">
          <i className="fab fa-x-twitter" />
          X
        </SocialLink>
        <YouTubeDropdown ref={ytRef}>
          <YouTubeButton onClick={() => setYtOpen(!ytOpen)}>
            <i className="fab fa-youtube" />
            YouTube
            <i className={`fas fa-chevron-${ytOpen ? 'up' : 'down'}`} style={{ width: 'auto', fontSize: '0.65rem', marginLeft: 'auto' }} />
          </YouTubeButton>
          <YouTubeSubMenu $open={ytOpen}>
            <YouTubeSubLink href="https://youtube.com/@A5omic" target="_blank" rel="noopener noreferrer">
              Personal
            </YouTubeSubLink>
            <YouTubeSubLink href="https://youtube.com/@A5omic_" target="_blank" rel="noopener noreferrer">
              Music
            </YouTubeSubLink>
          </YouTubeSubMenu>
        </YouTubeDropdown>
        {socialLinks.map((link) => (
          <SocialLink
            key={link.label}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className={link.icon} />
            {link.label}
          </SocialLink>
        ))}
      </LinkList>
    </Container>
  );
};

export default SocialLinks; 